import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { dictionary } from './dictionary'
import type { Language, Strings } from './types'

export const LANGUAGE_STORAGE_KEY = 'lm-language'

interface LanguageApi {
  language: Language
  setLanguage: (language: Language) => void
  strings: Strings
}

const LanguageContext = createContext<LanguageApi | null>(null)

export function useLanguage(): LanguageApi {
  const api = useContext(LanguageContext)
  if (!api) throw new Error('useLanguage must be used inside a LanguageProvider')
  return api
}

function isLanguage(value: unknown): value is Language {
  return value === 'es' || value === 'en'
}

/**
 * Resolution order: stored preference wins, then the browser locale
 * (English only on an `en` prefix, everything else falls back to Spanish),
 * then Spanish when there is nothing to read (SSR / tests without navigator).
 * Components not yet migrated keep rendering their hardcoded Spanish, so the
 * provider never rewrites anything global (no `document.lang` mutation).
 */
export function detectLanguage(): Language {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
      if (isLanguage(stored)) return stored
    }
  } catch {
    // Private-mode / SSR storage failures fall through to navigator.
  }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      if (navigator.language.toLowerCase().startsWith('en')) return 'en'
    }
  } catch {
    // A hostile navigator shim must not break rendering.
  }
  return 'es'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage)

  const setLanguage = useCallback((next: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
    } catch {
      // Persist on a best-effort basis; the in-memory switch still applies.
    }
    setLanguageState(next)
  }, [])

  const api = useMemo<LanguageApi>(
    () => ({ language, setLanguage, strings: dictionary[language] }),
    [language, setLanguage]
  )

  return <LanguageContext.Provider value={api}>{children}</LanguageContext.Provider>
}
